import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { VoiceModeContext } from '@trinity/chat-components/containers/TextToSpeechAudioPlayer/Context.js';
import { ChatQueryStatus, SocketNamespace } from '@trinity/chat';
import { QuerySocketEvent } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import { getQuerySiblings } from '@trinity/chat-components/utils';
import { createLogger } from '@trinity/chat-components/websocket/utils';
import type { WS } from '@trinity/chat';
import { ChatSessionQueriesContext, QuerySocketContext } from './context';
import { useScrollToBottomOfMessages } from './hooks/useScrollMessageList';

type Props = {
  children: React.ReactNode;
  pollingEnabed?: boolean;
  queryIdentifier: string;
};

type QueryAvailable = {
  chatInstance: Chat.Instance;
  query: Chat.Query;
  regenerated: boolean;
};

type QueryErrored = {
  langfuseTraceUrl: string | null;
  queryIdentifier: string;
  traceId: string;
};

const useGetQuerySiblings = () => {
  const queries = useContext(ChatSessionQueriesContext);

  return useCallback((queryIdentifier: string) => {
    return getQuerySiblings({
      queryIdentifier,
      queries,
    });
  }, [queries]);
};

export const QueryEventsContainer = ({ queryIdentifier, children, pollingEnabed = true }: Props) => {

  const scrollToBottom = useScrollToBottomOfMessages();
  const dispatch = useContext(ChatStateDispatchContext);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const getQuerySiblings = useGetQuerySiblings();
  const voicemode = useContext(VoiceModeContext);

  const logger = useMemo(() => createLogger({
    namespace: SocketNamespace.Queries,
  }), []);

  const handleQueryErrored = useCallback((data: QueryErrored) => {
    dispatch({
      type: 'query-errored',
      payload: {
        langfuseTraceUrl: data.langfuseTraceUrl,
        queryIdentifier: data.queryIdentifier,
        traceId: data.traceId,
      },
    });
    scrollToBottom();
  }, [dispatch, scrollToBottom]);

  const handleQueryAvailable = useCallback((data: QueryAvailable) => {
    if (data.regenerated) {
      dispatch({
        type: 'query-variant-response-available',
        payload: {
          chatInstance: data.chatInstance,
          query: data.query,
        },
      });
    } else {
      dispatch({
        type: 'query-response-available',
        payload: {
          chatInstance: data.chatInstance,
          query: data.query,
        },
      });
      if (voicemode.enabled && data.query.response) {
        logger.log(`Enqueuing voice mode for query: ${data.query.id}`);
        voicemode.enqueue({
          queryId: data.query.id,
          type: data.query.response.type,
        });
      }
    }
    scrollToBottom();
  }, [dispatch, scrollToBottom, voicemode]);

  const handleQueryStreaming = useCallback((data: WS.QueryResponseStreaming.Data) => {
    dispatch({
      type: 'query-response-streaming',
      payload: {
        queryIdentifier: data.queryIdentifier,
        textChunk: data.textChunk,
      },
    });

    scrollToBottom();
  }, [dispatch, scrollToBottom]);

  const apiClient = useChatApiClient();

  const getPendingQuery = useCallback(() => {
    return apiClient.chat.getPendingQuery({
      queryIdentifier,
    }).then(res => {
      if (!res.query) return;

      let keepPolling = false;
      if (res.query.status === ChatQueryStatus.Error) {
        logger.log(`Polling: Query errored - ${queryIdentifier}`);
        handleQueryErrored({
          queryIdentifier: res.query.identifier,
          langfuseTraceUrl: res.query.langfuseTraceUrl,
          traceId: null,
        });
      } else if (res.query.status === ChatQueryStatus.Complete || res.query.status === ChatQueryStatus.FollowupsAvailable) {
        logger.log(`Polling: Query available - ${queryIdentifier} Status: ${res.query.status}`);
        // if (!res.chatInstance.name) {
        //   keepPolling = true;
        //   logger.log(`Polling: Chat instance name missing, continuing poll - ${queryIdentifier}`);
        // } else {

        const siblings = getQuerySiblings(res.query.mapping.parentIdentifier);
        const regenerated = siblings.length > 1;

        logger.log(`Query regenerated: ${regenerated}, Siblings: ${siblings.length}`);

        handleQueryAvailable({
          chatInstance: res.chatInstance,
          query: res.query,
          regenerated,
        });
        // }
      } else {
        logger.log(`Polling: Query pending - ${queryIdentifier} Status: ${res.query.status}`);
        keepPolling = true;
      }

      if (keepPolling) {
        const pollingInterval = 2000;
        pollingTimeoutRef.current = setTimeout(() => {
          logger.log(`Polling for query - ${queryIdentifier}`);
          return getPendingQuery();
        }, pollingInterval);
      }
    });
  }, [apiClient, getQuerySiblings, handleQueryAvailable, handleQueryErrored, queryIdentifier]);

  useEffect(() => {
    if (!pollingEnabed) return;

    if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);

    pollingTimeoutRef.current = setTimeout(() => {
      logger.log(`Start polling for query ${queryIdentifier}`);
      return getPendingQuery();
    }, 1000);

    return () => {
      if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    };
  }, [getPendingQuery, queryIdentifier, pollingEnabed]);

  const handleQueryAvailableEvent = useCallback((data: WS.QueryAvailable.Data) => {
    if (data.queryIdentifier !== queryIdentifier) {
      logger.log(`WebSocket: Ignoring QueryAvailable - expected: ${queryIdentifier}, received: ${data.queryIdentifier}`);
      return;
    }

    if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);

    return apiClient.queries.getQuery({
      chatInstanceId: data.chatId,
      queryIdentifier: data.queryIdentifier,
    }).then(res => {
      handleQueryAvailable({
        chatInstance: res.chatInstance,
        query: res.query,
        regenerated: data.regenerated,
      });
    });
  }, [apiClient, handleQueryAvailable, logger, queryIdentifier]);

  const handleQueryResponseStreamingEvent = useCallback((data: WS.QueryResponseStreaming.Data) => {
    if (data.queryIdentifier !== queryIdentifier) return;
    handleQueryStreaming(data);
  }, [handleQueryStreaming, queryIdentifier]);

  const handleTakingTooLongEvent = useCallback((data: WS.QueryTakingTooLong.Data) => {
    if (data.identifier !== queryIdentifier) {
      logger.log(`WebSocket: Ignoring QueryTakingTooLong - expected: ${queryIdentifier}, received: ${data.identifier}`);
      return;
    }
    dispatch({
      type: 'query-taking-long',
      payload: {
        queryIdentifier: data.identifier,
      },
    });
  }, [dispatch, logger, queryIdentifier]);

  const handleQueryErroredEvent = useCallback((data: WS.QueryErrored.Data) => {
    if (data.queryIdentifier !== queryIdentifier) {
      logger.log(`WebSocket: Ignoring QueryErrored - expected: ${queryIdentifier}, received: ${data.queryIdentifier}`);
      return;
    }

    handleQueryErrored(data);
  }, [handleQueryErrored, logger, queryIdentifier]);

  // Use refs to store latest handlers to avoid re-registering listeners
  const handlersRef = useRef({
    handleQueryAvailableEvent,
    handleQueryResponseStreamingEvent,
    handleQueryErroredEvent,
    handleTakingTooLongEvent,
  });

  useEffect(() => {
    handlersRef.current = {
      handleQueryAvailableEvent,
      handleQueryResponseStreamingEvent,
      handleQueryErroredEvent,
      handleTakingTooLongEvent,
    };
  });

  const socket = useContext(QuerySocketContext);

  useEffect(() => {
    if (!socket) {
      logger.log(`Socket: No socket available for query - ${queryIdentifier}`);
      return;
    };

    logger.log(`Socket: Setting up listeners for query - ${queryIdentifier}`);

    // Create stable callback references that use the latest handlers from ref
    const stableQueryAvailable = (data: WS.QueryAvailable.Data) => handlersRef.current.handleQueryAvailableEvent(data);
    const stableQueryStreaming = (data: WS.QueryResponseStreaming.Data) => handlersRef.current.handleQueryResponseStreamingEvent(data);
    const stableQueryErrored = (data: WS.QueryErrored.Data) => handlersRef.current.handleQueryErroredEvent(data);
    const stableTakingTooLong = (data: WS.QueryTakingTooLong.Data) => handlersRef.current.handleTakingTooLongEvent(data);

    socket.on(QuerySocketEvent.QueryAvailable, stableQueryAvailable);
    socket.on(QuerySocketEvent.QueryResponseStreaming, stableQueryStreaming);
    socket.on(QuerySocketEvent.QueryErrored, stableQueryErrored);
    socket.on(QuerySocketEvent.QueryTakingTooLong, stableTakingTooLong);

    return () => {
      logger.log(`Socket: Cleaning up listeners for query - ${queryIdentifier}`);
      socket.off(QuerySocketEvent.QueryAvailable, stableQueryAvailable);
      socket.off(QuerySocketEvent.QueryResponseStreaming, stableQueryStreaming);
      socket.off(QuerySocketEvent.QueryErrored, stableQueryErrored);
      socket.off(QuerySocketEvent.QueryTakingTooLong, stableTakingTooLong);
    };
  }, [socket, queryIdentifier]);

  return (
    <>
      {children}
    </>
  );
};